/** Correct streak counter — animated badge showing consecutive correct answers in the toolbar. */
import { useMemo } from 'react';
import { motion } from 'motion/react';
import { Zap } from 'lucide-react';
import { useSession } from '../SessionContext';

/** Minimum consecutive correct answers before showing the counter. */
const MIN_STREAK = 2;

/** Duration for the wrapper to expand/collapse (matches progress bar flex transition). */
const EXPAND_DURATION = 0.4;

/**
 * Counts consecutive correct answers ending at the most recently answered step.
 * Resets on any incorrect answer or unanswered gap.
 */
function useCorrectStreak(): { streak: number; isReviewing: boolean } {
  const { state } = useSession();

  const streak = useMemo(() => {
    let count = 0;
    for (let i = state.currentIndex; i >= 0; i--) {
      const answered = state.answeredSteps.get(i);
      if (!answered) break;
      const step = state.steps[i];
      if (step?.stepKind === 'info') continue;
      if (answered.result.isCorrect) {
        count++;
      } else {
        break;
      }
    }
    return count;
  }, [state.answeredSteps, state.currentIndex, state.steps]);

  return { streak, isReviewing: state.isReviewing };
}

/**
 * Live consecutive-correct counter. Appears after 2+ correct in a row.
 * Not gamification — just real-time feedback on flow state.
 *
 * Two-phase animation:
 * 1. Wrapper expands via maxWidth (progress bar shrinks smoothly)
 * 2. Content fades in after the expansion completes
 */
export function CorrectStreak() {
  const { streak, isReviewing } = useCorrectStreak();
  const show = streak >= MIN_STREAK && !isReviewing;

  return (
    <motion.div
      initial={false}
      animate={{ maxWidth: show ? 160 : 0 }}
      transition={{ duration: EXPAND_DURATION, ease: [0.16, 1, 0.3, 1] }}
      style={{ overflow: 'hidden', flexShrink: 0 }}
    >
      <motion.div
        initial={false}
        animate={{ opacity: show ? 1 : 0 }}
        transition={{
          duration: 0.3,
          delay: show ? EXPAND_DURATION : 0,
          ease: 'easeOut',
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--f-space-1)',
          padding: 'var(--f-space-1) var(--f-space-3)',
          borderRadius: 'var(--f-radius-full)',
          background: 'linear-gradient(90deg, #ff9b4a, #ff6eb1, #8a6cf0, #ff6eb1, #ff9b4a)',
          backgroundSize: '600px 100%',
          backgroundRepeat: 'repeat',
          animation: 'f-gradient-cycle 16s linear infinite',
          color: '#fff',
          fontSize: 'var(--f-text-xs)',
          fontWeight: 600,
          fontFamily: 'var(--f-font-sans)',
          letterSpacing: 'var(--f-tracking-normal)',
          whiteSpace: 'nowrap',
        }}
      >
        <Zap size={12} />
        <motion.span
          key={streak}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: show ? EXPAND_DURATION : 0, ease: 'easeOut' }}
        >
          {streak} in a row
        </motion.span>
      </motion.div>
    </motion.div>
  );
}
